import React, { Component } from 'react';
import { parse as qs_parse } from 'query-string';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import { action, observable, reaction } from 'mobx';
import { authorize } from 'utils/auth';
import PaginationOptions from 'utils/domain/PaginationOptions';
import SortingOptions from 'utils/domain/SortingOptions';
import { FormattedMessage } from 'ui/i18n';
import { INTERNAL_ERROR, BAD_REQUEST } from 'config/constants';
import { getIndex } from 'utils/helpers';
import { getYesNoValue } from 'api/helpers';

import {
	render_amount,
	render_date,
	render_solo_item,
	render_yes_no,
	render_editable_number,
	render_quantity,
	render_holdInvWeeks,
} from 'utils';

import {
	PageHeader,
	Loader,
	DataTable,
	ItemContentHeader,
	ChooseColor,
	ErrorLoadingContent,
	InfoBox,
	Alert,
	InfoBar,
} from 'ui/components';

import './Offerings.scss';

function getColumns(toggleModal, onChangeQty, onBlurHandler) {
	return [
		{
			prop: 'number',
			title: <FormattedMessage id="itemsTable.soloItemPackNameColumn" />,
			sortable: true,
			render(row) {
				return render_solo_item(
					row[this.prop],
					row,
					toggleModal,
					true,
					true,
					true
				);
			},
		},
		{
			prop: 'start_date',
			title: <FormattedMessage id="itemsTable.startDateColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_date(row[this.prop]);
			},
			noWrap: true,
		},
		{
			prop: 'end_date',
			title: <FormattedMessage id="itemsTable.endDateColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_date(row[this.prop]);
			},
			noWrap: true,
		},
		{
			prop: 'quantity',
			title: <FormattedMessage id="itemsTable.qtyColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_quantity(row[this.prop]);
			},
		},
		{
			prop: 'list_price',
			title: <FormattedMessage id="itemsTable.listColumn" />,
			align: 'right',
			sortable: true,
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'promo_price',
			title: <FormattedMessage id="itemsTable.promoColumn" />,
			align: 'right',
			sortable: true,
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'ta',
			title: <FormattedMessage id="itemsTable.TAColumn" />,
			sortable: true,
			align: 'right',
			render(row) {
				return `${row[this.prop]}%`;
			},
		},
		{
			prop: 'net_price',
			title: <FormattedMessage id="itemsTable.netColumn" />,
			align: 'right',
			sortable: true,
			render(row) {
				return render_amount(row[this.prop], row);
			},
			noWrap: true,
		},
		{
			prop: 'est_ship_date',
			title: <FormattedMessage id="itemsTable.estShipColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_date(
					row[this.prop],
					<FormattedMessage id="item.See_Components" />
				);
			},
		},
		{
			prop: 'invoice_hold_weeks',
			propToSort: 'displayed_invoice_hold_weeks',
			title: <FormattedMessage id="itemsTable.numberHoldInvWksColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_holdInvWeeks(row, row[this.prop]);
			},
		},
		{
			prop: 'is_drop_ship',
			title: <FormattedMessage id="itemsTable.dropShipColumn" />,
			align: 'center',
			sortable: true,
			render(row) {
				return render_yes_no(getYesNoValue(row[this.prop]));
			},
		},
		{
			prop: 'id',
			title: <FormattedMessage id="itemsTable.addToCartQtyColumn" />,
			align: 'center',
			render(row) {
				return render_editable_number(
					row,
					null,
					false,
					onChangeQty,
					onBlurHandler
				);
			},
		},
	];
}

@authorize({ rights: ['GetByCategoryId'] })
@inject('ProductsState', 'UIState', 'CartState', 'CartItemsState')
@observer
export default class OfferingsPage extends Component {
	static propTypes = {
		ProductsState: PropTypes.object.isRequired,
		UIState: PropTypes.object.isRequired,
		CartState: PropTypes.object.isRequired,
		CartItemsState: PropTypes.object.isRequired,
		location: PropTypes.object.isRequired,
	};

	constructor(props) {
		super(props);

		reaction(
			() => [this.categoryId, this.order, this.pagination],
			() =>
				this.ProductsState.fetchOfferings({
					categoryId: this.categoryId,
					promotionId: this.promotionId,
					pagination: this.pagination,
					sorting: this.order,
				})
		);

		reaction(
			() => this.ProductsState.fetchOfferings.state.result,
			result =>
				result &&
				result.data.length &&
				this.props.UIState.setSelectedPromotion({
					category_id: result.data[0].category_id,
					promo_id: result.data[0].promotion_id,
					country_short_name: result.data[0].currency.country_short_name,
				})
		);

		reaction(
			() => this.categoryId,
			() => (this.pagination = new PaginationOptions())
		);
	}

	componentWillMount() {
		this.getUrlParams();
	}

	componentWillReceiveProps(nextProps) {
		this.getUrlParams();
		if (this.props.location.search !== nextProps.location.search) {
			this.CartItemsState.emptyCurrentCart();
			this.props.CartState.removeAPIError();
		}
	}

	componentWillUnmount() {
		this.props.UIState.deselectPromotionInMenu();
		this.CartItemsState.emptyCurrentCart();
		this.props.CartState.removeAPIError();
	}

	@observable order = new SortingOptions('number', 'asc');
	@observable pagination = new PaginationOptions();
	@observable categoryId = 0;
	@observable showModal = false;

	ProductsState = this.props.ProductsState;
	CartState = this.props.CartState;
	CartItemsState = this.props.CartItemsState;

	onChangeQty(value, item) {
		if (value) {
			item.quantity_ordered = value;
			item.errorMsgId = this.CartItemsState.getErrorMessageId(
				false,
				this.CartState.getAvailableQuantity(item, false),
				this.CartState.getTotalQuantityFromCart(item),
				item
			);
			this.CartItemsState.addToCurrentCart(item);
		} else {
			this.CartItemsState.deleteFromCart(item);
		}
	}

	onBlurHandler(item) {
		const index = getIndex(
			this.CartItemsState.cartUpdates,
			'number',
			item.number
		);

		if (
			index >= 0 &&
			item.is_configurable &&
			item.quantity_ordered &&
			!item.errorMsgId
		) {
			this.toggleModal(item);
		}
	}

	@action
	handleAddToCart() {
		this.showModal = false;

		if (this.CartItemsState.isCurrentCartEmpty) {
			this.CartItemsState.toggleWarningMessage();
		} else {
			this.CartState.addToCart(this.CartItemsState.cartUpdates).then(() => {
				this.CartItemsState.emptyCurrentCart();

				this.ProductsState.fetchOfferings({
					categoryId: this.categoryId,
					promotionId: this.promotionId,
					pagination: this.pagination,
					sorting: this.order,
					clearCache: true,
				});
			});
		}
	}

	@action
	getUrlParams() {
		this.categoryId = qs_parse(window.location.search).categoryId;
		this.promotionId = qs_parse(window.location.search).promotionId;
	}
	@action
	onOrderChange(order) {
		this.order = order;
	}

	@action
	onPaginationChange(pagination) {
		this.pagination = pagination;
	}

	@action
	toggleModal(itemDetails) {
		this.itemDetails = itemDetails;
		this.ProductsState.fetchOfferings({
			categoryId: this.categoryId,
			promotionId: this.promotionId,
			pagination: this.pagination,
			sorting: this.order,
		});
		this.showModal = !this.showModal;
	}

	render() {
		const { fetchOfferings } = this.ProductsState;
		return (
			<div className="Offerings">
				{fetchOfferings.handle({
					loading: () => <Loader />,
					rejected: error =>
						<div>
							{error.status === INTERNAL_ERROR || error.status === BAD_REQUEST
								? <ErrorLoadingContent />
								: <div>
										<PageHeader
											headerText={
												<span>
													<FormattedMessage id="offeringsPage.headerText" />
												</span>
											}
										/>
										<Alert type="danger" msgId={`offerings.${error.message}`} />
									</div>}
						</div>,
					resolved: results => {
						const [offering] = results.data;

						return (
							<div>
								{results.count > 0
									? <div>
											<PageHeader
												id="Offerings-page-header"
												headerText={`${offering.promotion_name} > ${offering.category_name}  `}
											/>

											<div className="Offerings-content">
												<ItemContentHeader
													printFunctionality={true}
													handleAddToCart={this.handleAddToCart.bind(this)}
													disableAddToCart={
														this.CartItemsState.hasError ||
														this.CartState.addToCart.state.loading ||
														offering.is_preview
													}
													name={offering.category_name}
													categoryId={offering.category_id}
													promotionId={offering.promotion_id}
												>
													<div
														id="Offerings-contentHeader-countResults"
														className="col-md-6 "
													>
														<p>
															<strong>
																{results.count}{' '}
																<FormattedMessage id="offeringsPage.itemsIn" />
															</strong>{' '}
															{offering.category_name}
														</p>
													</div>
												</ItemContentHeader>

												<div className="Please-note-info">
													<InfoBar>
														<p>
															<FormattedMessage id="home.pleaseNoteMessage" />
														</p>
													</InfoBar>
												</div>

												<DataTable
													id="Offerings-content-results-table"
													columns={getColumns(
														this.toggleModal.bind(this),
														this.onChangeQty.bind(this),
														this.onBlurHandler.bind(this)
													)}
													rows={results.data}
													order={this.order}
													onOrderChange={this.onOrderChange.bind(this)}
													count={results.count}
													pagination={this.pagination}
													onPaginationChange={this.onPaginationChange.bind(
														this
													)}
												/>

												{this.showModal &&
													<ChooseColor
														show={this.showModal}
														onRequestClose={this.toggleModal.bind(this)}
														item={this.itemDetails}
														baseNumberRequired={true}
														handleAddToCart={this.handleAddToCart.bind(this)}
													/>}
											</div>
										</div>
									: <InfoBox type="info">
											<FormattedMessage id="general.noItemsAvailable" />
										</InfoBox>}
							</div>
						);
					},
				})}
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/ui/pages/Offerings/index.js